"use client";

import { facings } from "@/info/facings";
import { spaceTypes } from "@/info/spaces";
import formikOptions from "@/utils/formikOptions";
import { CalendarDate } from "@internationalized/date";
import { deepEqual as isEqualFunc } from "fast-equals";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { Input } from "../rhf/input";
import { normalizeSingleDate } from "../rhf/normalize-date";
import { ButtonActions } from "./action-buttons";
import { useFormContext } from "./context/FormProvider";
function getMonthsBetweenTwoDates(date1: Date, date2: Date = new Date()): number {
  const totalMonthsDate1 = 12 * date1.getFullYear() + date1.getMonth();
  const totalMonthsDate2 = 12 * date2.getFullYear() + date2.getMonth();
  return totalMonthsDate2 - totalMonthsDate1;
}
const bedroomOptions = [{
  value: 1,
  label: "1 BHK"
}, {
  value: 2,
  label: "2 BHK"
}, {
  value: 3,
  label: "3 BHK"
}, {
  value: 4,
  label: "4 BHK"
}, {
  value: 5,
  label: "5 BHK"
}, {
  value: 6,
  label: "6 BHK"
}, {
  value: 7,
  label: "7 BHK"
}, {
  value: 8,
  label: "8 BHK"
}];
const bathroomOptions = [{
  value: 1,
  label: "1"
}, {
  value: 2,
  label: "2"
}, {
  value: 3,
  label: "3"
}, {
  value: 4,
  label: "4"
}, {
  value: 5,
  label: "5"
}, {
  value: 6,
  label: "6"
}, {
  value: 7,
  label: "7"
}, {
  value: 8,
  label: "8"
}];
const balconyOptions = [{
  value: 0,
  label: "0"
}, {
  value: 1,
  label: "1"
}, {
  value: 2,
  label: "2"
}, {
  value: 3,
  label: "3"
}, {
  value: 4,
  label: "4"
}, {
  value: 5,
  label: "5"
}, {
  value: 6,
  label: "6"
}, {
  value: 7,
  label: "7"
}];
const possessionStatusOptions = [{
  code: "preLaunch",
  name: "Pre-Launch",
  shortName: "Pre-Launch",
  description: "Launch Expected By",
  mappedField: "launchedOn",
  monthOptional: true
}, {
  code: "underConstruction",
  name: "Under Construction",
  shortName: "UC",
  description: "Construction Completion Expected By",
  mappedField: "constructedOn",
  monthOptional: false
}, {
  code: "readyToMoveIn",
  name: "Ready to Move In",
  shortName: "RTM",
  description: "Construction Completed On(Appx.)",
  mappedField: "constructedOn",
  monthOptional: false
}];
const sortedSpaceTypes = [...spaceTypes].sort((a, b) => {
  // Always put terrace first
  if (a.code === "terraces") return -1;
  if (b.code === "terraces") return 1;
  // keep original order
  return 0;
});
const additionalSpacesOptions = formikOptions(sortedSpaceTypes.filter(st => st.extra));
type BaiscDetailsFormData = {
  carpetArea?: number;
  subArea?: number;
  plotArea?: number;
  totalFloors?: number;
  floorNo?: number;
  numUnits?: number;
  bedrooms?: string;
  bathrooms?: string;
  balconies?: string;
  coveredParking?: number;
  numParking?: number;
  price?: number;
  possessionStatus?: string;
  facing?: string;
  plotFacing?: string;
  constructedOn?: Date | null;
  timeFrameToSell?: string;
  specialAttributes?: string[];
  constructedMonth?: CalendarDate | null;
  constructedYear?: CalendarDate | null;
};
interface StepAdditionalDetailsProps {
  onNext: (data: BaiscDetailsFormData) => void;
  onBack: () => void;
  propertyType?: string;
  isLoading?: boolean;
}
export function StepBasic({
  onNext,
  onBack,
  isLoading
}: StepAdditionalDetailsProps) {
  const {
    state,
    updateStepData
  } = useFormContext();

  // Get propertyType from context.profile, fallback to prop
  const contextProfile = state.profile as {
    propertyType?: {
      type?: string;
    };
  };
  const currentPropertyType = contextProfile.propertyType?.type || "";

  // Cast the additional data from context to the correct type
  const contextBasic = (state as {
    basic?: Partial<BaiscDetailsFormData>;
  })?.basic as Partial<BaiscDetailsFormData>;
  const defaultValues: BaiscDetailsFormData = useMemo(() => {
    const isApartment = currentPropertyType === "apartment";
    const isVilla = currentPropertyType === "villa";
    let specialAttributes = contextBasic?.specialAttributes;
    if (!specialAttributes && (isApartment || isVilla)) {
      specialAttributes = ["halls", "kitchens"];
    }
    return {
      ...contextBasic,
      specialAttributes,
      numParking: contextBasic?.numParking,
      floorNo: contextBasic?.floorNo,
      // Ensure constructedOn is properly converted to Date if it's a string
      constructedOn: contextBasic?.constructedOn ? typeof contextBasic.constructedOn === "string" ? new Date(contextBasic.constructedOn) : contextBasic.constructedOn : null,
      constructedMonth: contextBasic && "constructedMonth" in contextBasic ? normalizeSingleDate(contextBasic.constructedMonth as any) : normalizeSingleDate(contextBasic?.constructedOn as any),
      constructedYear: contextBasic && "constructedYear" in contextBasic ? normalizeSingleDate(contextBasic.constructedYear as any) : normalizeSingleDate(contextBasic?.constructedOn as any)
    };
  }, [contextBasic, currentPropertyType]);
  const form = useForm<BaiscDetailsFormData>({
    defaultValues,
    mode: "onChange"
  });
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState,
    reset,
    ...useFormProps
  } = form;

  // Watch form values
  const watchedValues = useWatch({
    control
  }) as BaiscDetailsFormData;
  // Memoize the watched values to prevent unnecessary re-renders
  const memoizedWatchedValues = useMemo(() => {
    return watchedValues || {};
  }, [watchedValues]);

  // Update context on form change with debounced updates
  const prevWatched = useRef(memoizedWatchedValues);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!isEqualFunc(prevWatched.current, memoizedWatchedValues)) {
      // Clear previous timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Debounce the update to prevent rapid changes
      updateTimeoutRef.current = setTimeout(() => {
        const valuesToSave = {
          ...memoizedWatchedValues
        };

        // Sync constructedOn if inputs are present
        // Priority: Year input provides year, Month input provides month
        const yearVal = valuesToSave.constructedYear;
        const monthVal = valuesToSave.constructedMonth;
        if (yearVal) {
          // Handle CalendarDate or number/string legacy
          const year = (yearVal as any).year || Number(yearVal);

          // Default to January if no month input
          // If monthVal exists (CalendarDate), use its month
          let month = 0;
          if (monthVal) {
            month = (monthVal as any).month ? (monthVal as any).month - 1 : 0;
          }

          // Day 1
          valuesToSave.constructedOn = new Date(year, month, 1);
        }
        updateStepData("basic", valuesToSave);
        prevWatched.current = memoizedWatchedValues;
      }, 300);
    }

    // Cleanup timeout on unmount
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [memoizedWatchedValues, updateStepData]);

  // Clamp coveredParking to not exceed numParking
  useEffect(() => {
    const numParking = Number(watch("numParking") ?? 0);
    const coveredParking = Number(watch("coveredParking") ?? 0);
    if (coveredParking > numParking) {
      setValue("coveredParking", numParking, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  }, [setValue, watch]);

  // Watch for propertyType from context (should be passed from context)
  const isApartment_0 = currentPropertyType === "apartment";
  const isHouse = currentPropertyType === "house";
  const isVilla_0 = currentPropertyType === "villa";
  const isPlot = currentPropertyType === "plot";
  const onSubmit = (data: BaiscDetailsFormData) => {
    if (!data.price || data.price <= 0) {
      toast.error("Price is required and must be greater than 0");
      return;
    }
    const {
      constructedMonth: monthVal_0,
      constructedYear: yearVal_0,
      ...processedData
    } = data;
    let finalDate: Date | undefined;
    if (yearVal_0) {
      const year_0 = (yearVal_0 as any).year || Number(yearVal_0);
      let month_0 = 0;
      // Default to January if month is not provided
      if (monthVal_0) {
        month_0 = (monthVal_0 as any).month ? (monthVal_0 as any).month - 1 : 0;
      }
      // Day 1
      finalDate = new Date(year_0, month_0, 1);
      processedData.constructedOn = finalDate;
    } else {
      processedData.constructedOn = undefined;
    }

    // Validation Logic
    if (finalDate && processedData.possessionStatus) {
      const status = processedData.possessionStatus;
      const monthsDiff = getMonthsBetweenTwoDates(finalDate);
      const statusConfig = possessionStatusOptions.find(s => s.code === status);
      const label = statusConfig?.description || "Date";
      if (monthsDiff > 0 && status !== "readyToMoveIn") {
        // Date is in the past, but status is not RTM
        toast.warning(`${label} is passed. Status should be Ready to Move In now.`);
        return;
      }
      if (status === "readyToMoveIn" && monthsDiff < 0) {
        // Date is in the future, status is RTM
        toast.warning(`${label} is a future date. Status cannot be Ready to Move In.`);
        return;
      }
    }
    onNext(processedData);
  };
  const canProceed = watchedValues.bedrooms && watchedValues.bathrooms && watchedValues.price && watchedValues.price > 0;
  const renderDateInputs = () => {
    // Determine current status configuration
    const currentStatus = watchedValues.possessionStatus;
    const statusConfig_0 = possessionStatusOptions.find(s_0 => s_0.code === currentStatus);
    if (!statusConfig_0) return null;
    const dateLabelBase = statusConfig_0.description || "Date";

    // Determine if month is required based on status and year range
    let isMonthRequired = !statusConfig_0.monthOptional;

    // If it's UC or RTM (monthOptional is false), check the year range condition
    // Rule: Mandatory only if year is within [Current-1, Current+1]
    if (isMonthRequired) {
      const yearVal_1 = watchedValues.constructedYear;
      if (yearVal_1) {
        const year_1 = (yearVal_1 as any).year || Number(yearVal_1);
        const currentYear = new Date().getFullYear();
        const isNearbyYear = year_1 >= currentYear - 1 && year_1 <= currentYear + 1;

        // If NOT nearby year, make it optional
        if (!isNearbyYear) {
          isMonthRequired = false;
        }
      } else {
        // We won't force mandatory month if no year is selected yet,
        // to avoid confusing validations before user has even picked a year.
        isMonthRequired = false;
      }
    }
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Input label={`${dateLabelBase} Month`} name="constructedMonth" type="date" granularity="month" control={control} rules={{
        required: isMonthRequired ? "Required" : false
      }} required={isMonthRequired} />
				<Input label={`${dateLabelBase} Year`} name="constructedYear" type="date" granularity="year" control={control} rules={{
        required: "Required"
      }} required placeholder="YYYY" />
			</div>;
  };
  return <FormProvider {...{
    control,
    handleSubmit,
    setValue,
    watch,
    formState,
    reset,
    ...useFormProps
  }}>
			<form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mx-auto p-2">
				{/* Configuration/Room fields */}
				{(isApartment_0 || isHouse || isVilla_0) && <>
						<Input control={control} type="buttongroup" expandable name="bedrooms" label="Bedrooms *" required options={bedroomOptions} />
						<Input control={control} type="buttongroup" expandable label="Bathrooms *" name="bathrooms" required increment={4} initialCount={4} options={bathroomOptions} />
						<Input control={control} type="buttongroup" label="Balconies" expandable increment={4} initialCount={5} name="balconies" options={balconyOptions} />
						<Input label="Additional Spaces" name="specialAttributes" type="checkbox" itemsClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" options={additionalSpacesOptions} control={control} />

						{/* Parking fields */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Input label="Parkings Available" name="numParking" type="number" min={0} control={control} />
							<Input label="Parkings Covered" name="coveredParking" type="number" min={0} rules={{
            validate: value => Number(value || 0) <= Number(watchedValues.numParking || 0) || "Covered parkings cannot exceed available parkings"
          }} max={watchedValues.numParking || undefined} control={control} />
						</div>
					</>}

				{/* Area fields */}
				{(isApartment_0 || isHouse || isVilla_0) && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<Input label="SUB Area (sqft)" name="subArea" placeholder="sq ft" type="number" min={0} control={control} />
						<Input label="Carpet Area (sqft)" name="carpetArea" type="number" min={0} placeholder="sq ft" control={control} />
						{(isPlot || isHouse || isVilla_0) && <Input label="Plot Area (sqft)" name="plotArea" type="number" min={0} placeholder="sq ft" control={control} />}
						{isApartment_0 && <Input label="On Floor" type="number" min={0} name="floorNo" control={control} />}
					</div>}
				{/* Price */}
				<Input label="Price *" name="price" type="inr" placeholder="Enter price" control={control} rules={{
        valueAsNumber: true,
        required: "Price is required"
      }} />
				{(isApartment_0 || isHouse || isVilla_0) && <Input label="Facing" name="facing" itemsClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2" type="radio" control={control} options={formikOptions(facings)} />}
				{(isPlot || isHouse || isVilla_0) && <Input label="Plot Facing" type="radio" itemsClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2" options={formikOptions(facings)} name="plotFacing" control={control} />}
				<Input label="Possession Status" name="possessionStatus" type="radio" itemsClassName="grid grid-cols-1 md:grid-cols-3 gap-2" options={formikOptions(possessionStatusOptions)} control={control} />
				{/* Date fields split into Month and Year */}
				{(isApartment_0 || isHouse || isVilla_0) && renderDateInputs()}
				<ButtonActions next disabled={!canProceed} onBack={onBack} isLoading={isLoading} />
			</form>
		</FormProvider>;
}